import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import useUser from "@saleor/hooks/useUser";
import { findValueInEnum, maybe } from "@saleor/misc";
import useBrandPrivateSearch from "@saleor/searches/useBrandPrivateSearch";
import { FeaturedInitialFormData } from "@saleor/store/components/StoreFeaturedStar";
import { useTenantFeaturedMutation } from "@saleor/store/mutations";
import { ListViews } from "@saleor/types";
import { SmopStatusEnum } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import { canManageTenants } from "@saleor/utils/handlers/userHandler";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import StoreListPage from "../../components/StoreListPage/StoreListPage";
import { useTenantListQuery } from "../../queries";
import {
  storeAddUrl,
  storeListUrl,
  StoreListUrlDialog,
  StoreListUrlQueryParams,
  storeUrl
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterVariables,
  saveFilterTab
} from "./filter";

interface StoreListProps {
  params: StoreListUrlQueryParams;
}

const StoreList: React.FC<StoreListProps> = ({ params }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const paginate = usePaginator();
  const shop = useShop();
  const { user } = useUser();
  const notify = useNotifier();

  const hasManageTenantPermission = canManageTenants();

  const currencySymbol = shop?.defaultCurrency || "PHP";

  const { updateListSettings, settings } = useListSettings(
    ListViews.STORE_LIST
  );

  React.useEffect(
    () =>
      navigate(
        storeListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  const paginationState = createPaginationState(settings.rowNumber, params);

  const checkStatus = () => {
    if (params.status === undefined) {
      return undefined;
    } else {
      return findValueInEnum(params.status, SmopStatusEnum);
    }
  };

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params, checkStatus()),
      status: maybe(() => findValueInEnum(params.status, SmopStatusEnum))
    }),
    [params]
  );

  const featuredTab = [
    ...[
      {
        data: {
          isFeatured: undefined
        },
        name: "All"
      },
      {
        data: {
          isFeatured: true
        },
        name: "Featured Stores"
      }
    ]
  ];

  // TODO: Update variable query
  const { data, loading, refetch } = useTenantListQuery({
    displayLoader: true,
    variables: {
      ...queryVariables,
      filter: {
        ...queryVariables.filter,
        brandId: params.brand ? [params.brand] : null
      }
    }
  });

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? featuredTab.length
        : 0
      : parseInt(params.activeTab, 0);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: storeListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    StoreListUrlDialog,
    StoreListUrlQueryParams
  >(navigate, storeListUrl, params);

  const handleTabChange = (tab: number | string) => {
    resetFilters();
    navigate(
      storeListUrl({
        ...params,
        activeTab: tab.toString(),
        ...featuredTab[tab].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(storeListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(featuredTab.length + 1);
  };

  // AUTOCOMPLETE SEARCH
  const brandInitialSearchData = {
    after: null,
    first: 20,
    query: ""
  };
  const {
    loadMore: loadMoreBrand,
    search: searchBrands,
    result: brandsQueryData
  } = useBrandPrivateSearch({
    variables: {
      ...brandInitialSearchData,
      accountManager: user.id
    }
  });
  const sellerBrandChoices = maybe(
    () => brandsQueryData?.data?.search?.edges,
    []
  ).map(edge => edge.node);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.tenantsPrivate.pageInfo),
    paginationState,
    params
  );

  const [brandLabel, setBrandLabel] = useState("");
  const getBrand = (brandID, choices) => {
    const getBrand = choices.find(brand => brand.id === brandID);
    if (getBrand) {
      setBrandLabel(getBrand.name + " - " + getBrand.legalName);
    }
  };

  useEffect(() => {
    if (sellerBrandChoices && params.brand) {
      getBrand(params.brand, sellerBrandChoices);
    }
  }, [sellerBrandChoices]);

  const filterOpts = getFilterOpts(params);

  const [updateIsFeatured] = useTenantFeaturedMutation({
    onCompleted: event => {
      const { errors } = event.tenantUpdate;
      if (errors.length > 0) {
        notify({
          status: "error",
          text: errors[0].message
        });
      }
      refetch();
    }
  });

  const handleSubmit = async (data: FeaturedInitialFormData) => {
    updateIsFeatured({
      variables: {
        id: data.id,
        input: { isFeatured: !data.isFeatured, paymentFeeRate: "1.11" }
      }
    });
    return null;
  };

  return (
    <>
      <WindowTitle
        title={intl.formatMessage({
          defaultMessage: "Store List",
          description: "Store List"
        })}
      />
      <StoreListPage
        onChangeBrand={brandId =>
          navigate(storeListUrl({ ...params, brand: brandId }))
        }
        brandLabel={brandLabel}
        brandID={params.brand}
        brands={sellerBrandChoices}
        tenants={maybe(
          () => data?.tenantsPrivate.edges.map(edge => edge.node),
          []
        )}
        currentTab={currentTab}
        currencySymbol={currencySymbol}
        fetchBrands={query => searchBrands(query)}
        fetchMoreBrands={{
          hasMore: maybe(
            () => brandsQueryData.data.search.pageInfo.hasNextPage
          ),
          loading: brandsQueryData.loading,
          onFetchMore: loadMoreBrand
        }}
        filterOpts={filterOpts}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onFilterChange={filter => changeFilters(filter, true)}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={featuredTab.map(featuredTab => featuredTab.name)}
        settings={settings}
        onAdd={() => navigate(storeAddUrl())}
        onRowClick={id => () => navigate(storeUrl(id))}
        disabled={loading}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        onSubmit={handleSubmit}
        pageInfo={pageInfo}
        customAction={undefined}
        hasManageTenantPermission={hasManageTenantPermission}
      />
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={maybe(() => featuredTab[currentTab].name, "...")}
      />
    </>
  );
};

export default StoreList;
